import { useTranslation } from 'react-i18next';

import { useMetaErrorNoticeBillingCohort } from 'src/hooks/featureFlags/useMetaErrorNoticeBillingCohort.hook';
import { bodyKeyForBillingCohort, META_ERROR_NOTICE_NAMESPACE } from 'src/utils/metaErrorNotice';
import { tMetaErrorNotice } from 'src/utils/metaErrorNotice.types';

import {
  useScheduledDateFormat,
  useScheduledDayFormat,
} from '../../../FollowUpBubble/FollowUpBubble.hook';
import MetaErrorNotice from './MetaErrorNotice.component';
import { tMetaErrorNoticeInternalButton } from './MetaErrorNotice.types';
import useMetaErrorNoticeAction from './useMetaErrorNoticeAction.hook';
import { useUndeliveredAnalytics } from 'src/hooks/analytics/chat/useUndeliveredAnalytics.hook';
import useUnmappedNoticeTelemetry from './useUnmappedNoticeTelemetry.hook';

type tCatalogMetaErrorNoticeProps = {
  notice: tMetaErrorNotice;
  /** The failed chat event, for actions that act on it (Reintentar). */
  eventId: string;
  /** INT-1500 U9: what the surface itself offers next to the catalog's action. */
  secondaryAction?: tMetaErrorNoticeInternalButton;
};

/**
 * INT-1503: MetaErrorNotice filled from a resolved catalog entry (INT-1502),
 * with its action wired (INT-1504). INT-1500 U3: an unmapped code names
 * itself in the body and is reported; Meta's details show as "Detalle
 * técnico". INT-1500 U7: a hold's body says when Meta rejected ("25 sep") and
 * when the hold lifts ("hoy 18:00"), like the scheduled card does.
 */
export default function CatalogMetaErrorNotice(
  props: tCatalogMetaErrorNoticeProps,
) {
  const { t } = useTranslation(META_ERROR_NOTICE_NAMESPACE);
  const { notice } = props;
  const action = useMetaErrorNoticeAction(notice, props.eventId);
  const formatMoment = useScheduledDateFormat();
  const formatDay = useScheduledDayFormat();
  useUnmappedNoticeTelemetry(notice, props.eventId);
  const { trackNoticeOpened } = useUndeliveredAnalytics();
  // RET-2256: 131049/130472 read "saldo" for the wallet cohort, "créditos"
  // for credits, and no unit while the cohort has not resolved.
  const billingCohort = useMetaErrorNoticeBillingCohort();
  const bodyKey = bodyKeyForBillingCohort(notice, billingCohort);

  const bodyValues = notice.bodyDates
    ? {
        ...notice.bodyValues,
        failedOn: formatDay(notice.bodyDates.failedOn),
        until: formatMoment(notice.bodyDates.until),
      }
    : notice.bodyValues;

  return (
    <MetaErrorNotice
      variant={notice.variant}
      title={t(notice.titleKey)}
      code={notice.displayCode}
      codeHref={notice.docsUrl}
      text={t(bodyKey, bodyValues)}
      steps={notice.stepKeys.map((key) => t(key))}
      action={action}
      secondaryAction={props.secondaryAction}
      note={notice.noteKey ? t(notice.noteKey) : undefined}
      visibleNote={notice.visibleNoteKey ? t(notice.visibleNoteKey) : undefined}
      learnMore={
        notice.learnMoreLink
          ? {
              label: t(notice.learnMoreLink.labelKey),
              href: notice.learnMoreLink.href,
            }
          : undefined
      }
      technicalDetail={notice.technicalDetail}
      onHowToOpen={() => trackNoticeOpened(notice.displayCode ?? notice.code)}
    />
  );
}
